/**
 * TryItOut API Service
 *
 * Lightweight API client for the unauthenticated "Try It Out" endpoints.
 * Does NOT use the authenticated APIService — uses only x-api-key and
 * X-TryItOut-Fingerprint headers (no JWT / Bearer token).
 */
import { getFingerprint } from "../utils/fingerprint";
import { API_BASE_URL } from "../../../config/apiConfig";

const BASE_URL = API_BASE_URL;
const API_KEY = process.env.REACT_APP_API_KEY || "API_KEY not defined";

const DEFAULT_TIMEOUT = 45000; // 45 seconds (analysis can take a moment)

/**
 * Internal helper — build common headers for every TryItOut request.
 */
async function buildHeaders() {
  const fingerprint = await getFingerprint();
  const headers = {
    "Content-Type": "application/json",
    "X-TryItOut-Fingerprint": fingerprint,
  };
  if (API_KEY && API_KEY !== "API_KEY not defined") {
    headers["x-api-key"] = API_KEY;
  }
  return headers;
}

/**
 * Internal helper — execute a fetch with timeout + standard error handling.
 */
async function request(method, path, body = null) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

  try {
    const headers = await buildHeaders();
    const options = {
      method,
      headers,
      signal: controller.signal,
    };
    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(`${BASE_URL}${path}`, options);
    clearTimeout(timeoutId);

    // Structured error from backend
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage =
        errorData?.message ||
        errorData?.error ||
        `Request failed (${response.status})`;

      const err = new Error(errorMessage);
      err.status = response.status;
      err.data = errorData;
      throw err;
    }

    return await response.json();
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw err;
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

const TryItOutAPI = {
  /**
   * Analyze a text message / SMS for scam indicators.
   * @param {string} phoneNumber - Optional phone number
   * @param {string} textMessage - The message text
   * @param {string} context     - Optional user-provided context
   */
  analyzeText: (phoneNumber, textMessage, context) =>
    request("POST", "/api/v1/public/tryitout/analyze-text", {
      phoneNumber: phoneNumber || null,
      textMessage: textMessage || null,
      context: context || null,
    }),

  /**
   * Analyze an email for scam indicators.
   * @param {string} senderEmail - Sender address
   * @param {string} subject     - Email subject
   * @param {string} body        - Email body
   */
  analyzeEmail: (senderEmail, subject, body) =>
    request("POST", "/api/v1/public/tryitout/analyze-email", {
      senderEmail: senderEmail || null,
      subject: subject || null,
      body: body || null,
    }),

  /**
   * Check a phone number for scam reports.
   * @param {string} phoneNumber - Phone number to check
   * @param {string} context     - Optional context
   */
  checkPhoneNumber: (phoneNumber, context) =>
    request("POST", "/api/v1/public/tryitout/phone-check", {
      phoneNumber,
      context: context || null,
    }),

  /**
   * Check a URL for safety.
   * @param {string} url     - The URL to analyse
   * @param {string} context - Optional context
   */
  checkURL: (url, context) =>
    request("POST", "/api/v1/public/tryitout/url-check", {
      url,
      context: context || null,
    }),

  /**
   * Get current usage / remaining quota (read-only).
   */
  getUsage: () => request("GET", "/api/v1/public/tryitout/usage"),
};

export default TryItOutAPI;
